/**
 * ProposedEnhancementsBanner — Wide, glowing call-to-action that sits
 * directly above the InteractiveMap on the `/app` landing page.
 *
 * UX brief: "as wide as almost the entire width of the screen, long for
 * about an inch, in a nice and different font — should glow light blue
 * and once hovered over with the cursor over it [pulse]. When we click
 * on it, it should open the full screen all the crucial enhancements"
 *
 * Iter-71 amplifier: the banner now wears an explicit "✨ Click me"
 * pulse pill on the right so ownership visitors don't miss the
 * affordance. Glow keyframe runs faster (5s) and brighter; hover lifts
 * the card 2px and intensifies the halo even more.
 */
import { Sparkles, MousePointerClick } from 'lucide-react';

export default function ProposedEnhancementsBanner({ onOpen }) {
  return (
    <>
      <style>{`
        @keyframes atrium-enh-pulse {
          0%, 100% {
            box-shadow:
              0 0 0 1px rgba(31, 79, 190, 0.30),
              0 6px 22px -6px rgba(31, 79, 190, 0.50),
              0 0 50px -6px rgba(163, 212, 212, 0.75);
            transform: scale(1);
          }
          50% {
            box-shadow:
              0 0 0 2px rgba(31, 79, 190, 0.50),
              0 14px 44px -4px rgba(31, 79, 190, 0.75),
              0 0 100px 4px rgba(163, 212, 212, 1);
            transform: scale(1.005);
          }
        }
        @keyframes atrium-enh-shimmer {
          0% { background-position: -250% 0; }
          100% { background-position: 250% 0; }
        }
        @keyframes atrium-clickme-bounce {
          0%, 100% { transform: translateY(0) rotate(-4deg); }
          50% { transform: translateY(-3px) rotate(4deg); }
        }
        @keyframes atrium-clickme-pill-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(255, 207, 51, 0.6); }
          70%      { box-shadow: 0 0 0 12px rgba(255, 207, 51, 0); }
        }
        .atrium-enh-banner:hover {
          animation-play-state: paused;
          box-shadow:
            0 0 0 2px rgba(31, 79, 190, 0.65),
            0 22px 56px -6px rgba(31, 79, 190, 0.9),
            0 0 140px 8px rgba(163, 212, 212, 1) !important;
          transform: translateY(-2px) scale(1.008) !important;
        }
      `}</style>
      <section className="w-full px-3 sm:px-6 lg:px-10 pt-8 pb-4">
        <button
          type="button"
          data-testid="proposed-enhancements-banner"
          onClick={onOpen}
          className="atrium-enh-banner block w-full rounded-3xl text-left group"
          style={{
            background:
              'linear-gradient(135deg, #0A1A2F 0%, #1F4FBE 45%, #1B3A7A 100%)',
            animation: 'atrium-enh-pulse 5s ease-in-out infinite',
            minHeight: '104px',
            transition: 'transform 0.25s ease, box-shadow 0.25s ease',
          }}
        >
          <div
            className="rounded-3xl px-6 sm:px-10 py-5 sm:py-7 flex items-center justify-between gap-4 sm:gap-6 relative overflow-hidden"
            style={{
              background:
                'linear-gradient(105deg, rgba(255,255,255,0.04) 0%, rgba(163,212,212,0.16) 50%, rgba(255,255,255,0.04) 100%)',
              backgroundSize: '250% 100%',
              animation: 'atrium-enh-shimmer 9s linear infinite',
            }}
          >
            {/* Left ornament */}
            <div className="hidden md:flex items-center gap-3 flex-shrink-0">
              <span
                className="block h-px w-14"
                style={{ background: 'linear-gradient(90deg, transparent 0%, #A3D4D4 100%)' }}
              />
              <Sparkles className="w-5 h-5" style={{ color: '#FFCF33' }} />
            </div>

            {/* Headline */}
            <div className="flex-1 text-center">
              <p
                className="text-[10px] sm:text-[11px] uppercase tracking-[0.32em] font-bold mb-1"
                style={{ color: '#A3D4D4', fontFamily: 'Manrope' }}
              >
                Ownership Deck · Curated Investment Opportunities
              </p>
              <h2
                className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl italic leading-tight"
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  color: 'white',
                  fontWeight: 500,
                  textShadow: '0 1px 22px rgba(163, 212, 212, 0.65)',
                }}
              >
                Proposed Crucial Enhancements
              </h2>
              <p
                className="text-[11px] sm:text-xs mt-1.5 italic group-hover:opacity-100 opacity-90 transition-opacity"
                style={{ color: '#A3D4D4', fontFamily: "'Cormorant Garamond', serif" }}
              >
                Renderings, ROI math, and the executive case for every signature project — designed to brief, decide, and approve in one sitting.
              </p>
            </div>

            {/* "Click Me" pill — bright yellow, pulses + bounces so it
                cannot be missed at first glance. Inside the
                button so the entire banner remains clickable. */}
            <div
              className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full font-bold uppercase text-[10px] sm:text-xs tracking-wider"
              style={{
                background: 'linear-gradient(135deg, #FFCF33 0%, #FFB400 100%)',
                color: '#0A1A2F',
                fontFamily: 'Manrope',
                animation: 'atrium-clickme-pill-pulse 1.8s ease-out infinite',
              }}
              data-testid="proposed-enhancements-clickme"
            >
              <MousePointerClick
                className="w-4 h-4"
                style={{ animation: 'atrium-clickme-bounce 0.9s ease-in-out infinite' }}
              />
              Click Me
            </div>
          </div>
        </button>
      </section>
    </>
  );
}
